import React, { useState, useEffect } from 'react';
import {
  Button,
  message,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  WalletOutlined,
  LeftOutlined,
  RightOutlined,
  DownOutlined,
  FilterOutlined,
  GiftOutlined,
  CloseOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { ExpenseTransactionModalMobile } from '../../../components/expenses/ExpenseTransactionModalMobile';
import { TransactionDetailModalMobile } from '../../../components/expenses/TransactionDetailModalMobile';
import { FilterModalMobile } from '../../../components/expenses/FilterModalMobile';
import { NotiModal } from '../../../components/Noti/NotiModal';
import {
  ExpenseTransaction,
  ExpenseCategory,
  getExpenseTransactions,
  getExpenseCategories,
  deleteExpenseTransaction,
  formatCurrency,
  // Thêm import cho wallet linking
  WalletLink,
  getExpenseWalletLinks,
} from '../../../services/expenseService';
import styles from './ExpensesPageMobile.module.css';

// Cấu hình locale tiếng Việt
dayjs.locale('vi');

export const ExpensesPageMobile: React.FC = () => {
  const [transactions, setTransactions] = useState<ExpenseTransaction[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [walletLinks, setWalletLinks] = useState<{ [key: number]: WalletLink[] }>({});
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<ExpenseTransaction | undefined>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<number | null>(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedTransaction, setSelectedTransaction] = useState<ExpenseTransaction | undefined>();
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    category_id: undefined as number | undefined,
    search: '',
    start_date: undefined as string | undefined,
    end_date: undefined as string | undefined,
  });
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadTransactions = async (reset = false) => {
    try {
      // Xác định trang cần load
      const pageToLoad = reset ? 1 : currentPage;
      
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const apiParams: any = {
        page: pageToLoad,
        limit: pageSize,
        category_id: filters.category_id,
        search: filters.search,
      };

      // Sử dụng start_date và end_date filter nếu có, nếu không thì dùng currentMonth
      if (filters.start_date || filters.end_date) {
        if (filters.start_date) {
          apiParams.start_date = filters.start_date;
        } else {
          apiParams.start_date = currentMonth.startOf('month').format('YYYY-MM-DD');
        }
        if (filters.end_date) {
          apiParams.end_date = filters.end_date;
        } else {
          apiParams.end_date = currentMonth.endOf('month').format('YYYY-MM-DD');
        }
      } else {
        apiParams.start_date = currentMonth.startOf('month').format('YYYY-MM-DD');
        apiParams.end_date = currentMonth.endOf('month').format('YYYY-MM-DD');
      }

      console.log('API Parameters:', apiParams);

      const response = await getExpenseTransactions(apiParams);
      
      console.log('API Response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', Object.keys(response));
      console.log('Transactions:', response.data);
      console.log('Pagination:', response.pagination);
      console.log('Data length:', response.data?.length);
      console.log('Pagination total:', response.pagination?.total);
      console.log('Pagination totalPages:', response.pagination?.totalPages);
      
      // Cập nhật dữ liệu
      if (reset) {
        setTransactions(response.data);
        setCurrentPage(1);
        // Load wallet links cho tất cả giao dịch mới
        loadWalletLinksForTransactions(response.data);
      } else {
        // Merge data mới với data cũ, tránh trùng lặp
        setTransactions(prev => {
          const existingIds = new Set(prev.map(transaction => transaction.id));
          const newTransactions = response.data.filter(transaction => !existingIds.has(transaction.id));
          const mergedTransactions = [...prev, ...newTransactions];
          // Load wallet links cho các giao dịch mới
          loadWalletLinksForTransactions(newTransactions);
          return mergedTransactions;
        });
      }
      
      // Cập nhật pagination info
      const pagination = response.pagination;
      if (pagination) {
        setTotal(pagination.total);
        setHasMore(pageToLoad < pagination.totalPages);
        console.log('Has more:', pageToLoad < pagination.totalPages, 'Current page:', pageToLoad, 'Total pages:', pagination.totalPages);
      } else {
        setHasMore(response.data.length === pageSize);
        console.log('No pagination, has more based on data length:', response.data.length === pageSize);
      }
    } catch (error) {
      console.error('Error in loadTransactions:', error);
      message.error('Không thể tải danh sách giao dịch');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load wallet links cho danh sách giao dịch
  const loadWalletLinksForTransactions = async (transactions: ExpenseTransaction[]) => {
    try {
      const linksPromises = transactions.map(async (transaction) => {
        try {
          const links = await getExpenseWalletLinks(transaction.id);
          return { transactionId: transaction.id, links };
        } catch (error) {
          console.error(`Error loading wallet links for transaction ${transaction.id}:`, error);
          return { transactionId: transaction.id, links: [] };
        }
      });

      const results = await Promise.all(linksPromises);
      const newWalletLinks: { [key: number]: WalletLink[] } = {};
      
      results.forEach(({ transactionId, links }) => {
        if (links.length > 0) {
          newWalletLinks[transactionId] = links;
        }
      });

      setWalletLinks(prev => ({ ...prev, ...newWalletLinks }));
    } catch (error) {
      console.error('Error loading wallet links:', error);
    }
  };

  // Load more function
  const loadMore = () => {
    console.log('LoadMore called - loadingMore:', loadingMore, 'hasMore:', hasMore, 'loading:', loading);
    if (!loadingMore && hasMore && !loading) {
      console.log('Incrementing current page from', currentPage, 'to', currentPage + 1);
      setCurrentPage(prev => prev + 1);
    } else {
      console.log('LoadMore blocked - loadingMore:', loadingMore, 'hasMore:', hasMore, 'loading:', loading);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await getExpenseCategories();
      setCategories(categoriesData);
    } catch (error) {
      message.error('Không thể tải danh mục');
    }
  };

  // Effect để load dữ liệu khi page thay đổi
  useEffect(() => {
    console.log('useEffect triggered - currentPage:', currentPage, 'pageSize:', pageSize);
    if (currentPage === 1) {
      loadTransactions(true); // Reset data khi về trang 1
    } else {
      loadTransactions(false); // Load more khi trang > 1
    }
  }, [currentPage, pageSize]);

  // Effect để reset pagination khi filter thay đổi
  useEffect(() => {
    console.log('Filter effect triggered - filters:', filters, 'currentMonth:', currentMonth);
    setCurrentPage(1);
    setHasMore(true);
    setTransactions([]);
    setTotal(0);
    setWalletLinks({});
    // Fetch dữ liệu mới với filter mới
    setTimeout(() => {
      loadTransactions(true);
    }, 100);
  }, [filters, currentMonth]);

  useEffect(() => {
    loadCategories();
  }, []);

  const handleAddTransaction = () => {
    setEditingTransaction(undefined);
    setIsModalOpen(true);
  };

  const handleEditTransaction = (record: ExpenseTransaction) => {
    setEditingTransaction(record);
    setIsModalOpen(true);
    setIsDetailModalOpen(false);
  };

  const handleDeleteTransaction = async (id: number) => {
    try {
      await deleteExpenseTransaction(id);
      message.success('Xóa giao dịch thành công');
      loadTransactions(true);
      setIsDetailModalOpen(false);
    } catch (error: any) {
      // Hiển thị thông báo chi tiết từ API
      const errorMessage = error.response?.data?.error || error.message || 'Có lỗi xảy ra khi xóa giao dịch';
      message.error(errorMessage);
    }
  };

  const handleShowDeleteModal = (transactionId: number) => {
    setTransactionToDelete(transactionId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (transactionToDelete) {
      await handleDeleteTransaction(transactionToDelete);
      setShowDeleteModal(false);
      setTransactionToDelete(null);
    }
  };

  const handleModalSuccess = () => {
    loadTransactions(true);
  };

  const getCategoryById = (id: number) => {
    return categories.find(c => c.id === id);
  };

  const getIconContainerClass = (categoryName: string) => {
    if (categoryName?.toLowerCase().includes('ăn') || categoryName?.toLowerCase().includes('food')) {
      return styles.iconContainerFood;
    } else if (categoryName?.toLowerCase().includes('di chuyển') || categoryName?.toLowerCase().includes('transport')) {
      return styles.iconContainerTransport;
    }
    return styles.iconContainerOther;
  };

  const getCategoryTagClass = (categoryName: string) => {
    if (categoryName?.toLowerCase().includes('ăn') || categoryName?.toLowerCase().includes('food')) {
      return styles.foodTag;
    } else if (categoryName?.toLowerCase().includes('di chuyển') || categoryName?.toLowerCase().includes('transport')) {
      return styles.transportTag;
    }
    return styles.otherTag;
  };

  const getCategoryIcon = (category: ExpenseCategory | undefined) => {
    if (!category) return '💰';
    return category.icon || '💰';
  };

  // Nhóm giao dịch theo ngày
  const groupTransactionsByDate = () => {
    const groups: { [key: string]: ExpenseTransaction[] } = {};
    
    // Sort transactions by expense_date DESC to maintain order
    const sortedTransactions = [...transactions].sort((a, b) => 
      new Date(b.expense_date).getTime() - new Date(a.expense_date).getTime()
    );
    
    sortedTransactions.forEach(transaction => {
      const date = dayjs(transaction.expense_date).format('DD/MM/YYYY');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(transaction);
    });

    return Object.entries(groups).sort(([a], [b]) => 
      dayjs(b, 'DD/MM/YYYY').diff(dayjs(a, 'DD/MM/YYYY'))
    );
  };

  const handleTransactionClick = (transaction: ExpenseTransaction) => {
    setSelectedTransaction(transaction);
    setIsDetailModalOpen(true);
  };

  // Throttle scroll để tránh gọi quá nhiều
  const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    console.log('Scroll percentage:', scrollPercentage, 'Has more:', hasMore, 'Loading more:', loadingMore);
    
    // Trigger load more khi scroll đến 80%
    if (scrollPercentage > 0.8 && hasMore && !loadingMore && !loading) {
      console.log('Triggering load more...');
      loadMore();
    }
  }, [hasMore, loadingMore, loading]);

  const renderWalletLinks = (transactionId: number) => {
    const links = walletLinks[transactionId];
    if (!links || links.length === 0) return null;

    return (
      <div style={{ marginTop: 8 }}>
        {links.map((link) => (
          <Tag
            key={link.id}
            color="blue"
            icon={<WalletOutlined />}
            style={{ marginBottom: 4 }}
          >
            {link.wallet_name} ({link.wallet_source_name}): {formatCurrency(link.amount)}
          </Tag>
        ))}
      </div>
    );
  };

  const renderTransactionCard = (transaction: ExpenseTransaction) => {
    const category = getCategoryById(transaction.category_id);
    const categoryName = category?.name || 'Không xác định';
    
    return (
      <div 
        key={transaction.id} 
        className={styles.transactionCard}
        onClick={() => handleTransactionClick(transaction)}
        style={{ cursor: 'pointer' }}
      >
        <div className={`${styles.iconContainer} ${getIconContainerClass(categoryName)}`}>
          {getCategoryIcon(category)}
        </div>
        
        <div className={styles.transactionDetails}>
          <p className={styles.transactionTitle}>
            {transaction.notes || `Chi tiêu cho ${categoryName}`}
          </p>
          <span className={`${styles.category} ${getCategoryTagClass(categoryName)}`}>
            {getCategoryIcon(category)} {categoryName}
          </span>
        </div>
        
        <p className={styles.amount}>
          -{formatCurrency(transaction.amount)}
        </p>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>
        <WalletOutlined />
      </div>
      <div className={styles.emptyText}>
        Chưa có giao dịch chi tiêu nào
      </div>
      <div className={styles.emptySubtext}>
        Bắt đầu thêm giao dịch đầu tiên của bạn
      </div>
    </div>
  );

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => prev.subtract(1, 'month'));
    setCurrentPage(1); // Reset về trang đầu khi đổi tháng
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => prev.add(1, 'month'));
    setCurrentPage(1); // Reset về trang đầu khi đổi tháng
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ 
      ...prev, 
      [key]: value 
    }));
    setCurrentPage(1); // Reset về trang đầu khi thay đổi filter
  };

  const handleDetailModalEdit = () => {
    if (selectedTransaction) {
      handleEditTransaction(selectedTransaction);
    }
  };

  const handleDetailModalDelete = () => {
    if (selectedTransaction) {
      handleShowDeleteModal(selectedTransaction.id);
      setIsDetailModalOpen(false);
    }
  };

  const handleListHeaderClick = () => {
    setShowCategoryFilter(!showCategoryFilter);
  };

  const handleCategorySelect = (categoryId: number | undefined) => {
    handleFilterChange('category_id', categoryId);
    setShowCategoryFilter(false);
  };

  const getSelectedCategoryName = () => {
    if (!filters.category_id) return 'Tất cả danh mục';
    const category = categories.find(c => c.id === filters.category_id);
    return category?.name || 'Tất cả danh mục';
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setIsFilterModalOpen(false);
  };

  const handleFilterApply = (newFilters: any) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset về trang đầu khi áp dụng filter
  };

  const getFilterCount = () => {
    let count = 0;
    if (filters.category_id) count++;
    if (filters.start_date) count++;
    if (filters.end_date) count++;
    if (filters.search) count++;
    return count;
  };

  return (
    <div className={styles.container}>
      <div className={styles.mobileShell}>
        {/* App Header */}
        <header className={styles.appHeader}>
          <div className={styles.dateSelector}>
            <LeftOutlined onClick={handlePreviousMonth} />
            <span>{currentMonth.format('MMMM YYYY')}</span>
            <RightOutlined onClick={handleNextMonth} />
          </div>
        </header>

        {/* Transaction List */}
        <main className={styles.transactionList}>
          <div className={styles.listHeader}>
            <div className={styles.headerLeft} onClick={handleListHeaderClick}>
              <h3>{getSelectedCategoryName()}</h3>
              <DownOutlined style={{ 
                transform: showCategoryFilter ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} />
            </div>
            <div className={styles.headerRight}>
              <div className={styles.addButton} onClick={handleAddTransaction}>
                <PlusOutlined />
              </div>
              <div className={styles.filterButton} onClick={handleFilterModalOpen}>
                <FilterOutlined />
                {getFilterCount() > 0 && (
                  <span className={styles.filterBadge}>{getFilterCount()}</span>
                )}
              </div>
            </div>
          </div>

          {/* Category Filter Dropdown */}
          {showCategoryFilter && (
            <div className={styles.categoryFilterDropdown}>
              <div 
                className={styles.categoryFilterOption}
                onClick={() => handleCategorySelect(undefined)}
              >
                <span>Tất cả danh mục</span>
              </div>
              {categories.map((category) => (
                <div 
                  key={category.id}
                  className={styles.categoryFilterOption}
                  onClick={() => handleCategorySelect(category.id)}
                >
                  <span>{getCategoryIcon(category)} {category.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* Scrollable Transaction Content */}
          <div className={styles.transactionContent} onScroll={handleScroll}>
            {transactions.length > 0 ? (
              <>
                {groupTransactionsByDate().map(([date, transactions]) => (
                  <div key={date} className={styles.transactionGroup}>
                    <p className={styles.groupDate}>{date}</p>
                    {transactions.map(renderTransactionCard)}
                  </div>
                ))}
                {loadingMore && (
                  <div className={styles.loadingMore}>
                    <div className={styles.loadingSpinner}></div>
                    <span>Đang tải thêm...</span>
                  </div>
                )}
                {!hasMore && transactions.length > 0 && (
                  <div className={styles.noMoreData}>
                    <span>Đã tải hết dữ liệu ({transactions.length}/{total} mục)</span>
                  </div>
                )}
              </>
            ) : (
              renderEmptyState()
            )}
          </div>
        </main>
      </div>

      {/* Modal thêm/sửa giao dịch */}
      <ExpenseTransactionModalMobile
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        transaction={editingTransaction}
        onSuccess={handleModalSuccess}
      />

      {/* Modal bộ lọc */}
      <FilterModalMobile
        isOpen={isFilterModalOpen}
        onClose={handleFilterModalClose}
        onApply={handleFilterApply}
        currentFilters={filters}
        categories={categories}
      />

      {/* Modal chi tiết giao dịch */}
      <TransactionDetailModalMobile
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        transaction={selectedTransaction}
        category={selectedTransaction ? getCategoryById(selectedTransaction.category_id) : undefined}
        onEdit={handleDetailModalEdit}
        onDelete={handleDetailModalDelete}
      />

      {/* Modal xác nhận xóa */}
      <NotiModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Xác nhận xóa"
        message="Bạn có chắc chắn muốn xóa giao dịch này?"
        confirmText="Xóa"
        cancelText="Hủy"
      />
    </div>
  );
};
